// Team names: one rule for showing a team and one for comparing names. The
// frontend twin of backend/users/team_names.py (same rules). The server
// sends displayName, ageGroup and listingLabel on the payloads that carry
// teams; these helpers read them first and fall back to the same rule from
// the fields a row already has.
//
//   displayTeamName(t)   "Oakland Soldiers 16U": the name without CACHE's
//                        " · 2026" season suffix, plus the age group of the
//                        league key when the name does not say one already.
//                        A year that is part of a real name ("Team
//                        Charlotte 2027") is never touched.
//   ageGroupOf(t)        "16U" / "U18" from the key only (t.ageGroup, a
//                        "competition|division" leagueKey, a catalogue
//                        key's division, a partner key's age segment, the
//                        partner age_group, an age-token level). Never from
//                        a name or a label.
//   stripSeasonSuffix(l) a league label without " · 2026" / " · 2025-26".
//   stripSeasonYear(n)   MATCHING ONLY: standalone season and class years
//                        dropped; never empties a name.
//   matchKey(name)       what two names are compared by ("oakland soldiers").
//   listingLabelOf(t)    a qualifier that tells two listings of one team
//                        apart: the league label or level words, never a
//                        data vendor's name.
import { US_STATES } from "./hometown";

const AGE_TOKEN = /^(?:(\d{1,2})\s?-?\s?u|u\s?-?\s?(\d{1,2}))$/i;
const AGE_TEXT = /(^|[^a-z0-9])(?:\d{1,2}\s?-?\s?u|u\s?-?\s?\d{1,2})(?![a-z0-9])/;
// An age group written apart ("u-17", "17 u"), joined into one token.
const AGE_APART = /(^|[^a-z0-9])(?:(\d{1,2})\s?-?\s?u|u\s?-?\s?(\d{1,2}))(?![a-z0-9])/g;
const AGE_WORD = /^(?:\d{1,2}u|u\d{1,2})$/;
const SEASON_SUFFIX = /\s*·\s*(?:19|20)\d{2}(?:\s*[-/–]\s*(?:\d{4}|\d{2}))?\s*$/;
const YEAR = "(?:199\\d|20[0-2]\\d|203[0-5])";
const SEASON_TOKEN = new RegExp(
  `(^|[^A-Za-z0-9])(?:(?:class\\s+of|c/o)\\s+)?'?${YEAR}(?:\\s*[-/–]\\s*(?:${YEAR}|\\d{2}))?(?![A-Za-z0-9])`,
  "gi",
);
const NOISE_WORDS = new Set(["hs", "school", "boys", "girls", "basketball", "varsity", "jv", "aau", "club"]);
const VENDOR = /\b(?:cerebro|sports\s?radar|sportradar|genius)\b/i;
const EMPTY_LABELS = new Set(["any league", "cache partner", "team code", "partner team"]);
const CATALOGUE_DIVISION = /__([a-z0-9]+?)(?:_\d+)?$/;
const STATE_NAMES = new Set(Object.keys(US_STATES).map((n) => n.toLowerCase()));
const STATE_CODES = new Set(Object.values(US_STATES));

const text = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");
const fold = (v) => text(v).normalize("NFKD").replace(/[̀-ͯ]/g, "");

// "16U" / "U18" for a value that is exactly an age token, else "".
const ageLabel = (value) => {
  const m = AGE_TOKEN.exec(text(value));
  if (!m) return "";
  return m[1] ? `${Number(m[1])}U` : `U${Number(m[2])}`;
};

const ageOfKey = (key) => {
  const k = text(key);
  if (!k) return "";
  if (k.includes("|")) return ageLabel(k.split("|").slice(1).join("|"));
  const low = k.toLowerCase();
  if (low.startsWith("cbx_")) {
    const m = CATALOGUE_DIVISION.exec(low);
    return m ? ageLabel(m[1]) : "";
  }
  if (low.startsWith("cache_")) {
    const parts = low.split("_");
    return parts.length >= 4 ? ageLabel(parts[2]) : "";
  }
  return "";
};

export const ageGroupOf = (t) => {
  if (!t || typeof t !== "object") return "";
  const own = ageLabel(t.ageGroup);
  if (own) return own;
  for (const key of [t.leagueKey, t.league_key, t.catalogueKey, t.cacheLeague, t.league]) {
    const found = ageOfKey(key);
    if (found) return found;
  }
  return ageLabel(t.age_group) || ageLabel(t.level);
};

export const hasAge = (name, age) => {
  const t = fold(name).toLowerCase();
  if (!t) return false;
  if (AGE_TEXT.test(t)) return true;
  const m = AGE_TOKEN.exec(text(age));
  if (!m) return false;
  const number = String(Number(m[1] || m[2]));
  return new RegExp(`(^|[^a-z0-9])${number}(?![a-z0-9])`).test(t);
};

export const stripSeasonSuffix = (label) => {
  const t = text(label);
  return t.replace(SEASON_SUFFIX, "").trim() || t;
};

export const stripSeasonYear = (name) => {
  const t = text(name);
  if (!t) return t;
  const out = t
    .replace(SEASON_TOKEN, (_m, lead) => `${lead} `)
    .replace(/[([]\s*[)\]]/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–·,|/]+|[\s\-–·,|/]+$/g, "")
    .trim();
  return /[A-Za-z0-9]/.test(out) ? out : t;
};

const isState = (value) => {
  const t = text(value).toLowerCase().replace(/[^a-z ]+/g, " ").split(/\s+/).filter(Boolean).join(" ");
  if (!t) return false;
  return t.length === 2 ? STATE_CODES.has(t.toUpperCase()) : STATE_NAMES.has(t);
};

const withoutState = (value) => {
  let t = value;
  for (let i = 0; i < 3 && t; i += 1) {
    const m = /(?:\(\s*([^()]*?)\s*\)|,\s*([^,()]*?))\s*$/.exec(t);
    if (!m || !isState(m[1] !== undefined ? m[1] : m[2])) break;
    t = t.slice(0, m.index).trim();
  }
  return t;
};

const nameTokens = (tokens) => {
  if (!tokens.length) return [];
  const kept = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok === "high" && tokens[i + 1] === "school") { i += 1; continue; }
    if (!NOISE_WORDS.has(tok) && !AGE_WORD.test(tok)) kept.push(tok);
  }
  return kept.length ? kept : [tokens.reduce((a, b) => (b.length > a.length ? b : a))];
};

export const matchKey = (name) => {
  let t = fold(name).toLowerCase().replace(/[’‘]/g, "'").trim();
  if (!t) return "";
  t = withoutState(stripSeasonYear(t));
  if (!t) return "";
  t = t.replace(/['.]/g, "");
  t = t.replace(AGE_APART, (_m, lead, a, b) => `${lead}${a ? `${a}u` : `u${b}`}`);
  return nameTokens(t.replace(/[^a-z0-9]+/g, " ").trim().split(/\s+/).filter(Boolean)).join(" ");
};

const nameOf = (t) => text(t?.teamName) || text(t?.name) || text(t?.currentTeam) || text(t?.team);

export const displayTeamName = (t) => {
  if (!t || typeof t !== "object") return "";
  if (text(t.displayName)) return text(t.displayName);
  const base = stripSeasonSuffix(nameOf(t));
  if (!base) return "";
  const age = ageGroupOf(t);
  return age && !hasAge(base, age) ? `${base} ${age}` : base;
};

const cleanLabel = (value) => {
  let label = stripSeasonSuffix(value);
  if (label.includes("|")) {
    const [competition, ...rest] = label.split("|");
    const division = rest.join("|").trim();
    const comp = competition.trim();
    label = division && !comp.toLowerCase().includes(division.toLowerCase()) ? `${comp} ${division}` : comp;
  }
  if (!label || EMPTY_LABELS.has(label.toLowerCase()) || VENDOR.test(label)) return "";
  return label;
};

// College or Pro from a level label. "HS / Grassroots" says nothing about a
// school or a club team, so it gives no words (the server's listingLabel,
// which knows the team's kind, comes first).
const levelWords = (t) => {
  const level = text(t?.level).toLowerCase();
  if (/college|ncaa|naia|juco|university/.test(level)) return "College";
  if (/\bpro\b|professional/.test(level)) return "Pro";
  return "";
};

export const listingLabelOf = (t) => {
  if (!t || typeof t !== "object") return "";
  const own = cleanLabel(t.listingLabel);
  if (own) return own;
  for (const value of [t.leagueLabel, t.leagueName, t.league_label, t.cascade?.leagueDisplayName]) {
    const label = cleanLabel(value);
    if (label) return label;
  }
  return levelWords(t);
};
